import * as React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Box, CircularProgress } from '@mui/material'
import NoMatch from '../../pages/NotFound'
import { useTokenClaims } from '../../security/useTokenClaims'
import { LaborProvider } from '../labor/LaborContext'
import LoggedInLayout from '../layout/LoggedInLayout'
import { ProjectProvider } from '../project/contexts/ProjectContext'
import { ShiftEndProvider } from '../reports/shift-end/ShiftEndContext'
import ShiftEndReportPage from '../../pages/ShiftEndReportPage'
import ShiftEndSelectSupervisionSelectionPage from '../../pages/ShiftEndSelectSupervisionPage'
import ShiftEndWorkItemsPartialPage from '../../pages/ShiftEndWorkItemsPartialPage'
import ShiftEndWorkersPartialPage from '../../pages/ShiftEndWorkersPartialPage'
import ConstraintsPage from '../../pages/ConstraintsPage'
import QcSelectionPage from '../../pages/QCSelectionPage'
import InspectionDetailPage from '../../pages/InspectionDetailPage'
import InspectionsLogPage from '../../pages/InspectionsLogPage'
import { useServiceWorkers } from '../hooks/useServiceWorkers'
import { AssignWorkProvider } from '../assignwork/AssignWorkContext'
import ShiftEndIndirectSelectionPage from '../../pages/ShiftEndIndirectSelectionPage'
import QuantitySurveys from '../../pages/QuantitySurveysPage'
import ChangeOrdersPage from '../../pages/ChangeOrdersPage'
import { useIndexedDB } from '../hooks/useIndexedDB'
import { cacheForemanData } from '../../services/OfflineServices/foremanCacheService'
import { usePolling } from '../hooks/usePolling'
import ScaffoldManagementPage from '../../pages/ScaffoldManagementPage'
import FleetEquipmentLogPage from '../../pages/FleetEquipmentLogPage'
import { usePermissionContext } from '../../security/PermissionContext'
import { routeData } from './RouteData'
import { useProjectFeaturesContext } from '../project/contexts/ProjectFeaturesContext'
import WorkEntryPage from '../../pages/WorkEntryPage'
import {
	filterRoutesForProjectTemplate,
	findRouteComponentsByPath,
	getDefaultRouteForRole,
	isRouteAvailableForTemplate,
	normalizePath,
	stripIndexRoutes,
} from './navigationUtils'

const ForemanSelectionPage = React.lazy(() => import('../../pages/ForemanSelectionPage'))
const ForemanDetailPage = React.lazy(() => import('../../pages/ForemanDetailPage'))
const WorkItemsPage = React.lazy(() => import('../../pages/WorkItemsPage'))
const LaborPage = React.lazy(() => import('../../pages/LaborPage'))
const ProjectsPage = React.lazy(() => import('../../pages/ProjectsPage'))
const ProjectDetailsPage = React.lazy(() => import('../../pages/ProjectDetailsPage'))
const ProjectImportsPage = React.lazy(() => import('../../pages/ProjectImportsPage'))
const ProjectExportsPage = React.lazy(() => import('../../pages/ProjectExportsPage'))
const ProjectFormPage = React.lazy(() => import('../../pages/ProjectFormPage'))
const ImportFileDetailsPage = React.lazy(() => import('../../pages/ImportFileDetailsPage'))
const ProjectsListPage = React.lazy(() => import('../../pages/ProjectsListPage'))
const ProjectNewPage = React.lazy(() => import('../../pages/ProjectNewPage'))
const WorkItemsOverviewPage = React.lazy(() => import('../../pages/WorkItemsOverviewPage'))
const WorkScopeRegisterPage = React.lazy(() => import('../../pages/WorkScopeRegisterPage'))
const AnalyticsPage = React.lazy(() => import('../../pages/AnalyticsPage'))
const ProjectSettingsPage = React.lazy(() => import('../../pages/ProjectSettingsPage'))
const ProjectPermissionsPage = React.lazy(() => import('../../pages/ProjectPermissionsPage'))
const ContractManagementPage = React.lazy(() => import('../../pages/ContractManagementPage'))
const ProjectPayrollPage = React.lazy(() => import('../../pages/ProjectPayrollPage'))
const ProjectSchedulePage = React.lazy(() => import('../../pages/ProjectSchedulePage'))
const SchedulePage = React.lazy(() => import('../../pages/SchedulePage'))
const PolicyEditPage = React.lazy(() => import('../../pages/PolicyEditPage'))
const MaterialPage = React.lazy(() => import('../../pages/MaterialPage'))
const PayrollPage = React.lazy(() => import('../../pages/TimeEntryPage'))
const AttendancePage = React.lazy(() => import('../../pages/AttendancePage'))
const TimeAcceptancePage = React.lazy(() => import('../../pages/TimeAcceptancePage'))

const Lazy = (children: React.ReactNode) => (
	<React.Suspense fallback={<>…</>}>{children}</React.Suspense>
)

const roleRoutes: Record<string, React.ReactNode[]> = {
	OfficeAdmin: [
		<Route
			path='contractManagement/*'
			key='oa-billing-log'
			element={Lazy(<ContractManagementPage />)}
		/>,
		<Route index key='oa-index' element={Lazy(<ProjectsListPage />)} />,
		<Route path='projects/*' key='oa-projects'>
			<Route index element={Lazy(<ProjectsListPage />)} />
			<Route path='new' element={Lazy(<ProjectNewPage />)} />
			<Route
				path=':id/*'
				element={Lazy(
					<ProjectProvider>
						<ProjectsPage />
					</ProjectProvider>,
				)}
			>
				<Route path='details' element={Lazy(<ProjectDetailsPage />)} />
				<Route path='schedule' element={Lazy(<ProjectSchedulePage />)} />
				<Route path='forms' element={Lazy(<ProjectFormPage />)} />
				<Route path='payroll' element={Lazy(<ProjectPayrollPage />)} />
				<Route path='payroll/pay-policies/new' element={Lazy(<PolicyEditPage />)} />
				<Route path='payroll/pay-policies/:policyId' element={Lazy(<PolicyEditPage />)} />
				<Route path='imports' element={Lazy(<ProjectImportsPage />)} />
				<Route path='imports/:id' element={Lazy(<ImportFileDetailsPage />)} />
				<Route path='exports' element={Lazy(<ProjectExportsPage />)} />
				<Route path='permissions' element={Lazy(<ProjectPermissionsPage />)} />
				<Route path='settings' element={Lazy(<ProjectSettingsPage />)} />
			</Route>
		</Route>,
		<Route path='backlog/*' key='oa-backlog'>
			<Route index element={Lazy(<ForemanSelectionPage />)} />
			<Route path=':id' element={Lazy(<ForemanDetailPage />)} />
		</Route>,
		<Route path='labor' key='oa-labor'>
			<Route
				index
				element={Lazy(
					<LaborProvider>
						<LaborPage />
					</LaborProvider>,
				)}
			/>
		</Route>,
		<Route path='schedule/*' key='oa-schedule' element={Lazy(<SchedulePage />)} />,
		<Route path='reports/*' key='oa-reports'>
			<Route path=':id?' element={<ShiftEndSelectSupervisionSelectionPage />} />
			<Route path=':id/Foreman' element={<ShiftEndReportPage />}>
				<Route path='workers' element={<ShiftEndWorkersPartialPage />} />
				<Route path='workitems' element={<ShiftEndWorkItemsPartialPage />} />
			</Route>
		</Route>,
		<Route
			path='scaffoldOrders/*'
			key='oa-scaffold-orders'
			element={Lazy(<ScaffoldManagementPage />)}
		/>,
		<Route path='equipment/*' key='oa-equipment' element={Lazy(<FleetEquipmentLogPage />)} />,
		<Route path='attendance/*' key='oa-attendance' element={Lazy(<AttendancePage />)} />,
		<Route
			path='timeAcceptance/*'
			key='oa-time-acceptance'
			element={Lazy(<TimeAcceptancePage />)}
		/>,
	],

	Foreman: [
		<Route
			path='contractManagement/*'
			key='foreman-billing-log'
			element={Lazy(<ContractManagementPage />)}
		/>,
		<Route index key='foreman-index' element={Lazy(<ForemanDetailPage />)} />,
		<Route path='backlog' key='foreman-backlog' element={Lazy(<ForemanDetailPage />)} />,
		<Route path='reports/*' key='foreman-reports' element={<ShiftEndReportPage />}>
			<Route path='workers' element={<ShiftEndWorkersPartialPage />} />
			<Route path='workitems' element={<ShiftEndWorkItemsPartialPage />} />
		</Route>,
		<Route path='payrollEntry/*' key='foreman-payroll-entry' element={Lazy(<PayrollPage />)} />,
		<Route path='attendance/*' key='foreman-attendance' element={Lazy(<AttendancePage />)} />,
		<Route path='projects/*' key='foreman-projects'>
			<Route index element={Lazy(<ProjectsListPage />)} />
		</Route>,
		<Route path='constraints' key='foreman-constraints'>
			<Route index element={<ConstraintsPage />} />
		</Route>,
		<Route path='workEntry/*' key='pc-work-entry' element={Lazy(<WorkEntryPage />)} />,
		<Route
			path='workScopeRegister/*'
			key='pc-work-scope-register'
			element={Lazy(<WorkScopeRegisterPage />)}
		/>,
	],

	QualityControl: [
		<Route index key='qc-index' element={Lazy(<InspectionDetailPage />)} />,
		<Route path='qcBacklog' key='qc-backlog' element={Lazy(<InspectionDetailPage />)} />,
	],

	QualityControlManager: [
		<Route index key='qcm-index' element={Lazy(<InspectionsLogPage />)} />,
		<Route path='inspections' key='qcm-inspections' element={Lazy(<InspectionsLogPage />)} />,
		<Route
			path='qcReports/:id?'
			key='qcm-qc-reports'
			element={Lazy(<ShiftEndIndirectSelectionPage department='QC' />)}
		/>,
	],

	ProjectControls: [
		<Route
			path='contractManagement/*'
			key='pc-billing-log'
			element={Lazy(<ContractManagementPage />)}
		/>,
		<Route
			index
			key='pc-index'
			element={Lazy(
				<AssignWorkProvider>
					<WorkItemsPage />
				</AssignWorkProvider>,
			)}
		/>,
		<Route
			path='workitems/overview/*'
			key='pc-workitems-overview'
			element={Lazy(<WorkItemsOverviewPage />)}
		/>,
		<Route path='analytics/*' key='pc-analytics' element={Lazy(<AnalyticsPage />)} />,
		<Route path='backlog/*' key='pc-backlog'>
			<Route index element={Lazy(<ForemanSelectionPage />)} />
			<Route path=':id' element={Lazy(<ForemanDetailPage />)} />
		</Route>,
		<Route path='projects/*' key='pc-projects'>
			<Route index element={Lazy(<ProjectsListPage />)} />
			<Route path='new' element={Lazy(<ProjectNewPage />)} />
			<Route
				path=':id/*'
				element={Lazy(
					<ProjectProvider>
						<ProjectsPage />
					</ProjectProvider>,
				)}
			>
				<Route path='details' element={Lazy(<ProjectDetailsPage />)} />
				<Route path='schedule' element={Lazy(<ProjectSchedulePage />)} />
				<Route path='forms' element={Lazy(<ProjectFormPage />)} />
				<Route path='payroll' element={Lazy(<ProjectPayrollPage />)} />
				<Route path='payroll/pay-policies/new' element={Lazy(<PolicyEditPage />)} />
				<Route path='payroll/pay-policies/:policyId' element={Lazy(<PolicyEditPage />)} />
				<Route path='imports' element={Lazy(<ProjectImportsPage />)} />
				<Route path='imports/:id' element={Lazy(<ImportFileDetailsPage />)} />
				<Route path='exports' element={Lazy(<ProjectExportsPage />)} />
				<Route path='permissions' element={Lazy(<ProjectPermissionsPage />)} />
				<Route path='settings' element={Lazy(<ProjectSettingsPage />)} />
			</Route>
		</Route>,
		<Route path='workitems/*' key='pc-workitems'>
			<Route
				index
				element={Lazy(
					<AssignWorkProvider>
						<WorkItemsPage />
					</AssignWorkProvider>,
				)}
			/>
			<Route
				path=':id'
				element={Lazy(
					<AssignWorkProvider>
						<WorkItemsPage />
					</AssignWorkProvider>,
				)}
			/>
		</Route>,
		<Route path='labor' key='pc-labor'>
			<Route
				index
				element={Lazy(
					<LaborProvider>
						<LaborPage />
					</LaborProvider>,
				)}
			/>
		</Route>,
		<Route path='schedule/*' key='pc-schedule' element={Lazy(<SchedulePage />)} />,
		<Route path='reports/*' key='pc-reports'>
			<Route path=':id?' element={<ShiftEndSelectSupervisionSelectionPage />} />
			<Route path=':id/Foreman' element={<ShiftEndReportPage />}>
				<Route path='workers' element={<ShiftEndWorkersPartialPage />} />
				<Route path='workitems' element={<ShiftEndWorkItemsPartialPage />} />
			</Route>
		</Route>,
		<Route path='constraints' key='pc-constraints'>
			<Route index element={<ConstraintsPage />} />
		</Route>,
		<Route path='qcBacklog' key='pc-qc-backlog'>
			<Route index element={Lazy(<QcSelectionPage />)} />
			<Route path=':id' element={Lazy(<InspectionDetailPage />)} />
		</Route>,
		<Route path='inspections' key='pc-inspections' element={Lazy(<InspectionsLogPage />)} />,
		<Route
			path='qcReports/:id?'
			key='pc-qc-reports'
			element={Lazy(<ShiftEndIndirectSelectionPage department='QC' />)}
		/>,
		<Route
			path='ltReports/:id?'
			key='pc-lt-reports'
			element={Lazy(<ShiftEndIndirectSelectionPage department='LT' />)}
		/>,
		<Route path='surveys/*' key='pc-surveys' element={Lazy(<QuantitySurveys />)} />,
		<Route path='changeOrders' key='pc-change-orders' element={Lazy(<ChangeOrdersPage />)} />,
		<Route path='material' key='pc-material' element={Lazy(<MaterialPage />)} />,
		<Route
			path='mReports/:id?'
			key='pc-m-reports'
			element={Lazy(<ShiftEndIndirectSelectionPage department='M' />)}
		/>,
		<Route
			path='scaffoldOrders/*'
			key='pc-scaffold-orders'
			element={Lazy(<ScaffoldManagementPage />)}
		/>,
		<Route path='equipment/*' key='pc-equipment' element={Lazy(<FleetEquipmentLogPage />)} />,
		<Route path='workEntry/*' key='pc-work-entry' element={Lazy(<WorkEntryPage />)} />,
		<Route
			path='workScopeRegister/*'
			key='pc-work-scope-register'
			element={Lazy(<WorkScopeRegisterPage />)}
		/>,
		<Route path='payrollEntry/*' key='pc-payroll-entry' element={Lazy(<PayrollPage />)} />,
		<Route path='attendance/*' key='pc-attendance' element={Lazy(<AttendancePage />)} />,
		<Route
			path='timeAcceptance/*'
			key='pc-time-acceptance'
			element={Lazy(<TimeAcceptancePage />)}
		/>,
	],

	Superintendent: [
		<Route
			path='contractManagement/*'
			key='sup-billing-log'
			element={Lazy(<ContractManagementPage />)}
		/>,
		<Route
			path='workitems/overview/*'
			key='sup-workitems-overview'
			element={Lazy(<WorkItemsOverviewPage />)}
		/>,
		<Route
			index
			key='sup-index'
			element={Lazy(
				<AssignWorkProvider>
					<WorkItemsPage />
				</AssignWorkProvider>,
			)}
		/>,
		<Route path='workitems/*' key='sup-workitems'>
			<Route
				index
				element={Lazy(
					<AssignWorkProvider>
						<WorkItemsPage />
					</AssignWorkProvider>,
				)}
			/>
			<Route
				path=':id'
				element={Lazy(
					<AssignWorkProvider>
						<WorkItemsPage />
					</AssignWorkProvider>,
				)}
			/>
		</Route>,
		<Route path='constraints' key='sup-constraints'>
			<Route index element={<ConstraintsPage />} />
		</Route>,
		<Route path='backlog/*' key='sup-backlog'>
			<Route index element={Lazy(<ForemanSelectionPage />)} />
			<Route path=':id' element={Lazy(<ForemanDetailPage />)} />
		</Route>,
		<Route path='reports/*' key='sup-reports'>
			<Route path=':id?' element={<ShiftEndSelectSupervisionSelectionPage />} />
			<Route path=':id/Foreman' element={<ShiftEndReportPage />}>
				<Route path='workers' element={<ShiftEndWorkersPartialPage />} />
				<Route path='workitems' element={<ShiftEndWorkItemsPartialPage />} />
			</Route>
		</Route>,
		<Route path='payrollEntry/*' key='sup-payroll-entry' element={Lazy(<PayrollPage />)} />,
		<Route path='attendance/*' key='sup-attendance' element={Lazy(<AttendancePage />)} />,
		<Route path='projects/*' key='sup-projects'>
			<Route index element={Lazy(<ProjectsListPage />)} />
		</Route>,
		<Route path='workEntry/*' key='pc-work-entry' element={Lazy(<WorkEntryPage />)} />,
		<Route
			path='workScopeRegister/*'
			key='pc-work-scope-register'
			element={Lazy(<WorkScopeRegisterPage />)}
		/>,
	],

	GeneralForeman: [
		<Route
			path='contractManagement/*'
			key='gf-billing-log'
			element={Lazy(<ContractManagementPage />)}
		/>,
		// same as Superintendent for now
		<Route
			path='workitems/overview/*'
			key='gf-workitems-overview'
			element={Lazy(<WorkItemsOverviewPage />)}
		/>,
		<Route
			index
			key='gf-index'
			element={Lazy(
				<AssignWorkProvider>
					<WorkItemsPage />
				</AssignWorkProvider>,
			)}
		/>,
		<Route path='backlog/*' key='gf-backlog'>
			<Route index element={Lazy(<ForemanSelectionPage />)} />
			<Route path=':id' element={Lazy(<ForemanDetailPage />)} />
		</Route>,
		<Route path='workitems/*' key='gf-workitems'>
			<Route
				index
				element={Lazy(
					<AssignWorkProvider>
						<WorkItemsPage />
					</AssignWorkProvider>,
				)}
			/>
			<Route
				path=':id'
				element={Lazy(
					<AssignWorkProvider>
						<WorkItemsPage />
					</AssignWorkProvider>,
				)}
			/>
		</Route>,
		<Route path='constraints' key='gf-constraints'>
			<Route index element={<ConstraintsPage />} />
		</Route>,
		<Route path='reports/*' key='gf-reports'>
			<Route path=':id?' element={<ShiftEndSelectSupervisionSelectionPage />} />
			<Route path=':id/Foreman' element={<ShiftEndReportPage />}>
				<Route path='workers' element={<ShiftEndWorkersPartialPage />} />
				<Route path='workitems' element={<ShiftEndWorkItemsPartialPage />} />
			</Route>
		</Route>,
		<Route path='payrollEntry/*' key='gf-payroll-entry' element={Lazy(<PayrollPage />)} />,
		<Route path='attendance/*' key='gf-attendance' element={Lazy(<AttendancePage />)} />,
		<Route path='projects/*' key='gf-projects'>
			<Route index element={Lazy(<ProjectsListPage />)} />
		</Route>,
		<Route path='workEntry/*' key='pc-work-entry' element={Lazy(<WorkEntryPage />)} />,
		<Route
			path='workScopeRegister/*'
			key='pc-work-scope-register'
			element={Lazy(<WorkScopeRegisterPage />)}
		/>,
	],

	LineTech: [
		<Route index key='lt-index' element={Lazy(<QuantitySurveys />)} />,
		<Route path='surveys/*' key='lt-surveys' element={Lazy(<QuantitySurveys />)} />,
	],
	LineTechManager: [
		<Route index key='ltm-index' element={Lazy(<QuantitySurveys />)} />,
		<Route path='surveys/*' key='ltm-surveys' element={Lazy(<QuantitySurveys />)} />,
		<Route
			path='ltReports/*'
			key='ltm-reports'
			element={Lazy(<ShiftEndIndirectSelectionPage department='LT' />)}
		/>,
	],
	MaterialManager: [
		<Route index key='mm-index' element={Lazy(<MaterialPage />)} />,
		<Route path='material/*' key='mm-material' element={Lazy(<MaterialPage />)} />,
		<Route
			path='mReports/*'
			key='mm-reports'
			element={Lazy(<ShiftEndIndirectSelectionPage department='M' />)}
		/>,
	],
	Material: [
		<Route index key='m-index' element={Lazy(<MaterialPage />)} />,
		<Route path='material/*' key='m-material' element={Lazy(<MaterialPage />)} />,
	],
}

export default function BrockRoutes() {
	const { role, id } = useTokenClaims()
	const { getPermissionBasedRoutes, getProjectRoutes, permissionsReady } = usePermissionContext()
	const { projectRoles, projectCrafts, projectTemplate, featuresReady } =
		useProjectFeaturesContext()
	useServiceWorkers(id)
	const db = useIndexedDB()
	usePolling(async () => {
		if (db) {
			await cacheForemanData(db)
		}
	})

	// Only block the first paint until permissions/features are ready. Later refetches must not
	// unmount `<Routes>` (that remounts AssignWorkProvider and can load the wrong worker).
	const [initialNavReady, setInitialNavReady] = React.useState(false)
	React.useEffect(() => {
		if (permissionsReady && featuresReady) {
			setInitialNavReady(true)
		}
	}, [permissionsReady, featuresReady])

	if (!initialNavReady) {
		return (
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					height: '100vh',
				}}
			>
				<CircularProgress color='success' />
			</Box>
		)
	}

	// Get role-based routes
	const roleBasedRoutes = filterRoutesForProjectTemplate(
		roleRoutes[role.title] ?? [],
		projectTemplate,
		role.title,
	)

	// Get role-based RouteDataItems
	const filterRole = role.title ?? 'Laborer'
	const roleBasedRouteDataItems = routeData
		.filter((route) => route.roles.includes(filterRole as any))
		.filter((route) => isRouteAvailableForTemplate(route, projectTemplate))

	// Get permission-based RouteDataItems
	const permissionBasedRouteDataItems = getPermissionBasedRoutes(
		roleBasedRouteDataItems,
		getProjectRoutes(projectRoles ?? [], projectCrafts ?? []),
	).filter((route) => isRouteAvailableForTemplate(route, projectTemplate))

	// Get Route components for permission-based routes
	const permissionBasedRouteComponents = filterRoutesForProjectTemplate(
		permissionBasedRouteDataItems.flatMap((route) =>
			findRouteComponentsByPath(route.routesPath, roleRoutes),
		),
		projectTemplate,
		role.title,
	)

	// When permissions grant a fuller route tree (e.g. projects/:id/*), drop the role's list-only copy.
	const permissionRoutePaths = new Set(
		permissionBasedRouteDataItems.map((route) => normalizePath(route.routesPath)),
	)
	const roleBasedRoutesWithoutPermissionOverrides = roleBasedRoutes.filter((route) => {
		if (!React.isValidElement(route) || typeof route.props.path !== 'string') return true
		return !permissionRoutePaths.has(normalizePath(route.props.path))
	})

	// Template 1 entry: redirect `/` to the role's default RouteData path (Time Entry / payrollEntry)
	const payrollEntryRoles = ['ProjectControls', 'Superintendent', 'GeneralForeman', 'Foreman']
	const defaultRoute = getDefaultRouteForRole(roleBasedRouteDataItems)
	const defaultRedirectPath = defaultRoute?.path.replace(/^\//, '')
	const shouldRedirectToDefault =
		projectTemplate === 1 && payrollEntryRoles.includes(role.title) && Boolean(defaultRedirectPath)

	const templateIndexRedirect = shouldRedirectToDefault ? (
		<Route
			index
			key='template-index-redirect'
			element={<Navigate to={defaultRedirectPath!} replace />}
		/>
	) : null

	const roleRoutesForRender = shouldRedirectToDefault
		? stripIndexRoutes(roleBasedRoutesWithoutPermissionOverrides)
		: roleBasedRoutesWithoutPermissionOverrides

	const children = [
		...roleRoutesForRender,
		...permissionBasedRouteComponents,
		templateIndexRedirect,
	].filter(Boolean) as React.ReactNode[]

	return (
		<ShiftEndProvider>
			<Routes>
				<Route path='/' element={<LoggedInLayout />}>
					{children}
					<Route path='*' element={<NoMatch />} />
				</Route>
			</Routes>
		</ShiftEndProvider>
	)
}
